import { useQuery } from '@tanstack/react-query';
import { makeBookingManagerApi } from 'services/BookingManagerApi/BookingManagerApi';
import {
  IBankTransaction,
  IBankTransactionsListResponse,
  IBankTransactionFilters,
  IPaymentMethodGroup,
  EBankTransactionProcessingStatus,
} from '../types';
import { ESortOrder } from 'store/common/types';

// Query key constants for cleanup
export const BANK_TRANSACTIONS_QUERY_KEY = 'bankTransactions';
export const PAYMENT_METHOD_GROUPS_QUERY_KEY = 'paymentMethodGroups';

/**
 * Default/empty filter state
 */
export const defaultFilters: IBankTransactionFilters = {
  startDate: null,
  endDate: null,
  paymentMethodGroupCode: null,
  type: null,
  statuses: [],
  search: '',
  bookingRef: '',
};

/**
 * Hook for fetching bank transactions with pagination, sorting, and filtering.
 */
export const useBankTransactionsQuery = (
  page: number, // 1-indexed (matches Pagination component)
  perPage: number,
  sortBy: string,
  sortOrder: ESortOrder,
  filters: IBankTransactionFilters = defaultFilters
) => {
  return useQuery<IBankTransactionsListResponse>({
    queryKey: [
      BANK_TRANSACTIONS_QUERY_KEY,
      page,
      perPage,
      sortBy,
      sortOrder,
      filters.startDate,
      filters.endDate,
      filters.paymentMethodGroupCode,
      filters.type,
      filters.statuses,
      filters.search,
      filters.bookingRef,
    ],
    queryFn: async () => {
      const api = makeBookingManagerApi();

      // Calculate offset from page (1-indexed)
      const offset = (page - 1) * perPage;

      // Build the filter object for the API
      const apiFilter: {
        term?: string;
        transactionDateFrom?: string;
        transactionDateTo?: string;
        amountCents?: 'positive' | 'negative';
        processingStatuses?: string[];
        paymentMethodGroupCode?: string;
        bookingRef?: string;
      } = {};

      if (filters.search && filters.search.length >= 3) {
        apiFilter.term = filters.search;
      }
      if (filters.startDate) {
        apiFilter.transactionDateFrom = filters.startDate;
      }
      if (filters.endDate) {
        apiFilter.transactionDateTo = filters.endDate;
      }
      if (filters.type) {
        apiFilter.amountCents = filters.type === 'CR' ? 'positive' : 'negative';
      }
      if (filters.statuses.length > 0) {
        apiFilter.processingStatuses = filters.statuses;
      }
      if (filters.paymentMethodGroupCode) {
        apiFilter.paymentMethodGroupCode = filters.paymentMethodGroupCode;
      }
      if (filters.bookingRef && filters.bookingRef.length >= 3) {
        apiFilter.bookingRef = filters.bookingRef;
      }

      // Make the API call
      const response = await api.searchBankTransactions({
        filter: Object.keys(apiFilter).length > 0 ? apiFilter : undefined,
        pagination: {
          offset,
          limit: perPage,
        },
        sort: {
          key: sortBy as 'transactionDate' | 'counterparty',
          order: sortOrder === ESortOrder.ASC ? 'asc' : 'desc',
        },
      });

      const transactions = response.data.bankTransactions as IBankTransaction[];
      const total = response.data.count;

      // Transform to the format expected by the UI
      return {
        data: transactions,
        meta: {
          total,
          page,
          perPage,
        },
      };
    },
  });
};

/**
 * Hook for fetching payment method groups (banks/payment methods).
 * Used for the bank filter dropdown.
 */
export const usePaymentMethodGroupsQuery = () => {
  return useQuery<IPaymentMethodGroup[]>({
    queryKey: [PAYMENT_METHOD_GROUPS_QUERY_KEY],
    queryFn: async () => {
      const api = makeBookingManagerApi();
      const response = await api.getPaymentMethodGroups();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since this rarely changes
  });
};
